/** Townhall API — 주간 타운홀 자료 히스토리. 열람·등록은 전사 직원, 수정·삭제는 등록자 본인. */

export interface TownhallMaterial {
  id: string
  kind: 'file' | 'link'
  name: string
  url: string | null
  ext: string | null
  sizeBytes: number | null
}

export interface TownhallRow {
  id: string
  /** 진행일 YYYY-MM-DD. */
  date: string
  year: number
  title: string | null
  /** 등록자 이메일. */
  createdBy: string | null
  /** 내가 등록한 타운홀이면 true — 수정·삭제는 본인만. */
  canEdit: boolean
  materials: TownhallMaterial[]
}

export interface TownhallList {
  maxMaterials: number
  uploadLimitBytes: number
  allowedExtensions: string[]
  townhalls: TownhallRow[]
}

export class TownhallError extends Error {}

const MESSAGES: Record<string, string> = {
  date_taken: '이 날짜의 타운홀이 이미 있어요.',
  invalid_date: '날짜를 다시 확인해 주세요.',
  invalid_url: 'http 또는 https로 시작하는 주소를 넣어 주세요.',
  materials_full: '타운홀 하나에 자료를 더 넣을 수 없어요.',
  unsupported_file: '지원하지 않는 파일 형식이에요.',
  too_large: '파일이 너무 커요.',
  empty_file: '빈 파일이에요.',
  storage_failed: '파일 저장에 실패했어요. 다시 시도해 주세요.',
  upload_failed: '업로드가 끊겼어요. 다시 시도해 주세요.',
  not_owner: '본인이 등록한 타운홀만 수정할 수 있어요.',
  employee_forbidden: '전사 직원만 사용할 수 있어요.',
  not_found: '타운홀을 찾을 수 없어요. 새로고침해 주세요.',
}

function failure(status: number, code?: string): TownhallError {
  return new TownhallError(MESSAGES[code ?? ''] ?? `요청에 실패했어요 (${status}).`)
}

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  })
  const data = (await res.json().catch(() => null)) as { error?: string } | null
  if (!res.ok) throw failure(res.status, data?.error)
  return data as T
}

const base = (id: string) => `/api/townhalls/${encodeURIComponent(id)}`

export function fetchTownhalls(): Promise<TownhallList> {
  return json('/api/townhalls')
}

export function createTownhall(input: { date: string; title: string }): Promise<TownhallRow> {
  return json('/api/townhalls', { method: 'POST', body: JSON.stringify(input) })
}

export function updateTownhall(
  id: string,
  input: { date: string; title: string },
): Promise<TownhallRow> {
  return json(base(id), { method: 'PATCH', body: JSON.stringify(input) })
}

export function deleteTownhall(id: string): Promise<{ ok: true }> {
  return json(base(id), { method: 'DELETE' })
}

export function addLink(id: string, input: { url: string; name: string }): Promise<TownhallRow> {
  return json(`${base(id)}/materials/link`, { method: 'POST', body: JSON.stringify(input) })
}

export function deleteMaterial(id: string, materialId: string): Promise<{ ok: true }> {
  return json(`${base(id)}/materials/${encodeURIComponent(materialId)}`, { method: 'DELETE' })
}

/** fetch 는 업로드 진행률을 주지 않으므로 XHR 을 쓴다. */
export function uploadFile(
  id: string,
  file: File,
  onProgress: (ratio: number) => void,
): Promise<TownhallRow> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${base(id)}/materials/file?filename=${encodeURIComponent(file.name)}`)
    xhr.responseType = 'json'
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as TownhallRow)
      else reject(failure(xhr.status, (xhr.response as { error?: string } | null)?.error))
    }
    xhr.onerror = () => reject(failure(0, 'upload_failed'))
    xhr.send(file)
  })
}

export function materialUrl(id: string, materialId: string, download = false): string {
  const url = `${base(id)}/materials/${encodeURIComponent(materialId)}/open`
  return download ? `${url}?download=1` : url
}
